/**
 * Pure rules the Azure Storage connection form READS (spec §4.1 / §4.2): which
 * sub-fields each auth mode needs, which "Connect using" mode the values are
 * in, and which services are enabled. What each control WRITES lives in
 * `azureStoragePreset.ts`.
 *
 * Kept out of the components so both `ConnectionFormFields` (shared rows) and
 * `AzureStorageConnectionFields` (the storage block) read one source of truth,
 * and so the rules are unit-testable without mounting the dialog.
 */
import type { AzureStorageEndpoints, StorageResourceRef, StorageService } from '@/types/azureStorage';
import { isLoopbackHost } from '../../../core/engines/azureStorageUrl';
import type { AzureStorageAuthType, ConnectionFormValues } from './connectionFormSchema';

/** "Connect using": a whole account, the local emulator, or one SAS-scoped resource. */
export type StorageConnectMode = 'account' | 'emulator' | 'resource';

/** Display order of the service checkboxes (spec §4.1 sketch). */
export const STORAGE_SERVICES: readonly StorageService[] = ['blob', 'table', 'queue', 'file'];

export const STORAGE_SERVICE_LABELS: Record<StorageService, string> = {
  blob: 'Blob',
  table: 'Table',
  queue: 'Queue',
  file: 'File shares',
};

/**
 * Azurite has no Files service. Order matches the emulator rule's in
 * `src/core/engines/azureStorageUrl.ts` so a pasted
 * `UseDevelopmentStorage=true` (or full Azurite) string and the preset produce the same record.
 */
export const AZURITE_SERVICES: readonly StorageService[] = ['blob', 'queue', 'table'];

const AZURITE_IMAGE = 'mcr.microsoft.com/azure-storage/azurite';
/** The ports Azurite listens on inside its container. */
const AZURITE_CONTAINER_PORTS: ReadonlyArray<{ service: StorageService; port: string }> = [
  { service: 'blob', port: '10000' },
  { service: 'queue', port: '10001' },
  { service: 'table', port: '10002' },
];

/** The host port a loopback endpoint dials (`http://127.0.0.1:20000/devstoreaccount1` → 20000); undefined otherwise. */
function loopbackHostPort(endpoint: string | undefined): string | undefined {
  if (!endpoint) return undefined;
  try {
    const url = new URL(endpoint);
    if (!isLoopbackHost(url.hostname)) return undefined;
    return url.port || (url.protocol === 'https:' ? '443' : '80');
  } catch {
    return undefined;
  }
}

/**
 * The `docker run` command the emulator hint shows and copies, as its pieces (each wraps as one): every service's
 * endpoint port published onto Azurite's own port — `-p 20000:10000` for a Blob endpoint on 127.0.0.1:20000 — so the
 * command starts an emulator the connection actually reaches. A missing or non-loopback endpoint keeps Azure's default
 * mapping (`-p 10000:10000`, the preset's).
 */
export function azuriteDockerRunParts(endpoints: AzureStorageEndpoints | undefined): string[] {
  const mappings = AZURITE_CONTAINER_PORTS.map(({ service, port }) => `-p ${loopbackHostPort(endpoints?.[service]) ?? port}:${port}`);
  return ['docker run -d', ...mappings, AZURITE_IMAGE];
}

export function azuriteDockerRun(endpoints?: AzureStorageEndpoints): string {
  return azuriteDockerRunParts(endpoints).join(' ');
}

/** The emulator preset's command: Azure's default emulator ports. */
export const AZURITE_DOCKER_RUN = azuriteDockerRun();

export const AZURE_STORAGE_AUTH_OPTIONS: ReadonlyArray<{ value: AzureStorageAuthType; label: string }> = [
  { value: 'Account Key', label: 'Account key' },
  { value: 'Shared Access Signature', label: 'Shared access signature' },
  { value: 'Microsoft Entra ID (Interactive Browser)', label: 'Microsoft Entra ID (Interactive Browser)' },
  { value: 'Azure CLI', label: 'Azure CLI (az login)' },
  { value: 'Service Principal', label: 'Service principal (client ID and secret)' },
  { value: 'Anonymous', label: 'Anonymous (public containers)' },
];

type StorageFormValues = Partial<ConnectionFormValues>;

/** The mode the values themselves are in. */
export function storageConnectMode(values: StorageFormValues): StorageConnectMode {
  if (values.azureStorageEmulator) return 'emulator';
  if (values.azureStorageScope?.kind === 'resource') return 'resource';
  return 'account';
}

/** The user's own "Connect using" pick, with what the values read right after it (transient form state, never saved). */
export type StorageModePick = { mode: StorageConnectMode; derivedAtPick: StorageConnectMode };

/**
 * The mode on screen. "Single resource" is a user intent before it is a value (its scope is written only once a resource
 * URL is pasted), so the pick (`azureStorageModePick`) holds while the values still read what they read right after it;
 * a change that moves them elsewhere (a pasted URL on blur, a pasted connection string) hands the choice back to the
 * values. Anonymous is always a single resource. A form VALUE rather than component state (C3): the engine's draft keeps
 * it across an engine round trip, a paste for another engine and the start view.
 */
export function storageModeOnScreen(values: StorageFormValues): StorageConnectMode {
  if (values.authentication === 'Anonymous') return 'resource';
  const derived = storageConnectMode(values);
  const pick = values.azureStorageModePick;
  return pick && pick.derivedAtPick === derived ? pick.mode : derived;
}

/** The pick written with `storageModeValues(mode)`: the values it leaves decide `derivedAtPick`. */
export function storageModePick(mode: StorageConnectMode, valuesAfterPick: StorageFormValues): StorageModePick {
  return { mode, derivedAtPick: storageConnectMode(valuesAfterPick) };
}

/** The secret's label per auth mode; `undefined` when the mode carries no secret. */
export function storageSecretLabel(auth: ConnectionFormValues['authentication']): string | undefined {
  if (auth === 'Account Key') return 'Account key:';
  if (auth === 'Shared Access Signature') return 'SAS token or connection string:';
  if (auth === 'Service Principal') return 'Client Secret:';
  return undefined;
}

export function storageShowsSecret(auth: ConnectionFormValues['authentication']): boolean {
  return storageSecretLabel(auth) !== undefined;
}

/** Only Service Principal carries a login (the app registration's client id). */
export function storageShowsLogin(auth: ConnectionFormValues['authentication']): boolean {
  return auth === 'Service Principal';
}

export function storageLoginLabel(): string {
  return 'Client ID:';
}

/** Enabled services: the explicit list, else all four (emulator: Azurite's three). */
export function storageEnabledServices(values: StorageFormValues): StorageService[] {
  if (values.azureStorageServices) return [...values.azureStorageServices];
  return values.azureStorageEmulator ? [...AZURITE_SERVICES] : [...STORAGE_SERVICES];
}

/** Tick / untick one service, re-sorted into the display order so the value is stable. */
export function toggleStorageService(
  enabled: readonly StorageService[],
  service: StorageService,
  next: boolean
): StorageService[] {
  const set = new Set(enabled);
  if (next) set.add(service);
  else set.delete(service);
  return STORAGE_SERVICES.filter((candidate) => set.has(candidate));
}

/** Human name of the scoped resource: the single-resource summary line and the paste preview (C3). */
export function describeStorageResource(resource: StorageResourceRef): string {
  switch (resource.kind) {
    case 'container': return `container "${resource.container}"`;
    case 'blobPrefix': return `folder "${resource.container}/${resource.prefix}"`;
    case 'blob': return `blob "${resource.container}/${resource.name}"`;
    case 'table': return `table "${resource.table}"`;
    case 'queue': return `queue "${resource.queue}"`;
    case 'share': return `file share "${resource.share}"`;
    case 'directory': return `directory "${resource.share}/${resource.path}"`;
    case 'file': return `file "${resource.share}/${resource.path}"`;
    case 'hnsPath': return `path "${resource.fileSystem}/${resource.path}"`;
    default: return 'the whole account';
  }
}
